import React, { useState } from 'react';
import { Card, CardActionArea, Typography, Box, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { resolveIcon, getCategoryLabel } from './appStoreUtils';
import AppDetailModal from './AppDetailModal';

const warmColor = '#e67e22';

const AppCard = React.memo(function AppCard({ app, selected, locked, onToggle }) {
  const [modalOpen, setModalOpen] = useState(false);

  const handleIconClick = (e) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          border: selected
            ? `2px solid ${warmColor}`
            : '2px solid #eee',
          transition: 'all 0.25s ease',
          position: 'relative',
          opacity: locked ? 0.9 : 1,
          '&:hover': locked ? {} : {
            borderColor: warmColor,
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 28px rgba(230, 126, 34, 0.18)`,
          },
        }}
      >
        {/* Overlay icona selezione */}
        {selected && !locked && (
          <CheckCircleIcon
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: warmColor,
              fontSize: 24,
              zIndex: 1,
            }}
          />
        )}
        {locked && (
          <LockIcon
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#95a5a6',
              fontSize: 20,
              zIndex: 1,
            }}
          />
        )}

        <CardActionArea
          onClick={() => !locked && onToggle(app.id)}
          disabled={locked}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: 200,
          }}
        >
          {/* Icona app */}
          <Box sx={{ mb: 1.5 }}>
            {resolveIcon(app)}
          </Box>

          {/* Nome */}
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {app.name}
          </Typography>

          {/* Chip categoria */}
          <Chip
            label={getCategoryLabel(app.category)}
            size="small"
            sx={{
              mb: 1,
              fontSize: '0.7rem',
              height: 22,
            }}
            color={locked ? 'default' : 'primary'}
            variant={locked ? 'filled' : 'outlined'}
          />

          {/* Descrizione troncata */}
          {app.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                fontSize: '0.8rem',
                lineHeight: 1.4,
              }}
            >
              {app.description.substring(0, 120)}
            </Typography>
          )}

          {/* Versione e dimensione */}
          <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            {app.version && (
              <Typography variant="caption" color="text.disabled">
                v{app.version}
              </Typography>
            )}
            {app.size && (
              <Typography variant="caption" color="text.disabled">
                {app.size}
              </Typography>
            )}
          </Box>
        </CardActionArea>

        {/* Info icon - angolo in basso a destra, fuori dall'area di selezione */}
        <Box
          onClick={handleIconClick}
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            zIndex: 2,
            cursor: 'pointer',
            borderRadius: '50%',
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            '&:hover': {
              bgcolor: '#f5f5f5',
              transform: 'scale(1.15)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#95a5a6' }} />
        </Box>
      </Card>

      {/* Modal dettagli app */}
      <AppDetailModal
        app={app}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
});

export default AppCard;
